import * as React from 'react';
import { z } from 'zod';
import { Button } from './Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { Form, FormField, useZodForm } from './Form';
import { Textarea } from './Input';
import { Inline } from './Layout';
import { ReasonPicker, type ReasonPickerOption } from './Picker';

export interface UrgentlyGoaModalProps {
  isOpen: boolean;
  /** The chosen reason and, when given, the trimmed notes. */
  onSubmit: (goaReason: string, notes?: string) => void;
  onCancel: () => void;
  /** The report is in flight: the controls lock and the button says so. */
  isSubmitting: boolean;
}

/** Urgently's gone-on-arrival reasons, as the partner API names them. */
const URGENTLY_GOA_REASONS: ReasonPickerOption[] = [
  { value: 'CUSTOMER_CANCELLED', label: 'Customer Cancelled' },
  { value: 'CUSTOMER_NOTREACHABLE', label: 'Customer Not Reachable' },
  { value: 'CUSTOMER_RESCHEDULE', label: 'Customer Reschedule' },
  { value: 'UNABLE_TO_LOCATE_VEHICLE', label: 'Unable to Locate Vehicle' },
  {
    value: 'UNABLE_TO_SERV_EQUIPMENT_NOT_SPECIFIED',
    label: 'Unable to Serve - Equipment Not Specified',
  },
  { value: 'UNABLE_TO_SERV_UNPAID', label: 'Unable to Serve - Unpaid' },
  {
    value: 'UNABLE_TO_SERVE_TOW_DISTANCE_TOO_FAR',
    label: 'Unable to Serve - Tow Distance Too Far',
  },
  {
    value: 'UNABLE_TO_SERVE_INCORRECT_PICKUP_LOCATION',
    label: 'Unable to Serve - Incorrect Pickup Location',
  },
  {
    value: 'UNABLE_TO_SERVE_INCORRECT_VEHICLE_INFO',
    label: 'Unable to Serve - Incorrect Vehicle Info',
  },
  {
    value: 'UNABLE_TO_SERVE_RESTRICTED_AREA',
    label: 'Unable to Serve - Restricted Area',
  },
  {
    value: 'CUSTOMER_ASSISTED_BY_ANOTHER_VENDOR',
    label: 'Customer Assisted by Another Vendor',
  },
  {
    value: 'CUSTOMER_ASSISTED_BY_AUTHORITIES',
    label: 'Customer Assisted by Authorities',
  },
  { value: 'OTHER', label: 'Other' },
];

const FORM_ID = 'urgently-goa-form';

/**
 * Notes are required only when the reason is "Other", which is the sort of
 * conditional the marker and the rule used to disagree about.
 */
export const urgentlyGoaSchema = z
  .object({
    goaReason: z.string().min(1, 'Please select a GOA reason'),
    notes: z.string(),
  })
  .refine((v) => v.goaReason !== 'OTHER' || v.notes.trim().length > 0, {
    path: ['notes'],
    message: 'Notes are required when selecting "Other"',
  });

/**
 * Reporting an Urgently job gone-on-arrival: a reason from the partner's
 * list and notes that become required when the reason is "Other".
 *
 *   <UrgentlyGoaModal isOpen={open} onSubmit={reportGoa}
 *     onCancel={close} isSubmitting={saving} />
 *
 * The form state is the dialog's own — it resets on cancel — so the screen
 * only sees the finished choice.
 */
export function UrgentlyGoaModal({
  isOpen,
  onSubmit,
  onCancel,
  isSubmitting,
}: UrgentlyGoaModalProps) {
  const [reasonOpen, setReasonOpen] = React.useState(false);
  const form = useZodForm(urgentlyGoaSchema, {
    defaultValues: { goaReason: '', notes: '' },
  });

  const handleCancel = () => {
    form.reset({ goaReason: '', notes: '' });
    setReasonOpen(false);
    onCancel();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="overflow-visible">
        <DialogHeader>
          <DialogTitle>Report GOA</DialogTitle>
        </DialogHeader>

        <Form
          form={form}
          gap={4}
          id={FORM_ID}
          onSubmit={(values) =>
            onSubmit(values.goaReason, values.notes.trim() || undefined)
          }
        >
          <FormField form={form} name="goaReason" label="Reason for GOA">
            {(field) => (
              <ReasonPicker
                options={URGENTLY_GOA_REASONS}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setReasonOpen(false);
                }}
                open={reasonOpen}
                onOpenChange={setReasonOpen}
                disabled={isSubmitting}
                maxHeight="sm"
              />
            )}
          </FormField>

          {/* Required only for "Other", which the schema decides and the
              marker follows — the two used to be set apart from each other. */}
          <FormField
            form={form}
            name="notes"
            label="Notes"
            required={form.watch('goaReason') === 'OTHER'}
          >
            {(field) => (
              <Textarea
                {...field}
                disabled={isSubmitting}
                placeholder="Enter any additional notes or details..."
                rows={3}
              />
            )}
          </FormField>
        </Form>

        <DialogFooter>
          <Inline grow justify="between">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button form={FORM_ID} type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Reporting...' : 'Report GOA'}
            </Button>
          </Inline>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
